import { useTranslation } from 'react-i18next'
import { PhoneNumberUtil } from 'google-libphonenumber'
import { useState, useEffect } from 'react'
import 'flatpickr/dist/themes/material_blue.css'
import { useFormik, FormikProvider } from 'formik'
import * as Yup from 'yup'
import PropTypes from 'prop-types'
import { getAgeGroupByDateOfBirth } from 'utils/common'
import Flatpickr from 'react-flatpickr'
import { NationalityDropdown } from 'components/custom/NationalityDropdown'
import { PhoneInput } from 'react-international-phone'
import 'react-international-phone/style.css'
import { parsePhoneNumberFromString } from 'libphonenumber-js'
import { CalendarIcon, DocumentArrowUpIcon, XMarkIcon } from '@heroicons/react/24/solid'
import { useLazyQuery } from '@apollo/client'
import { GET_COUNTRY_LIST } from 'graphql/common/queries'
import { uploadData } from 'aws-amplify/storage'
import { generateUniqueS3Key } from 'utils/common'

const phoneUtil = PhoneNumberUtil.getInstance()

// Helper function to check if a nationality is GCC
const isGCCNationality = (nationalityCode, countries) => {
    if (!nationalityCode || !countries) return false
    const country = countries.find(c => c.alpha_iso2 === nationalityCode)
    return country ? country.is_gcc : false
}

const isPhoneValid = phone => {
    if (!phone || phone.trim() === '') return true // Allow empty phone number
    try {
        return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone))
    } catch (error) {
        return false
    }
}

// Helper function to validate name input (only Arabic and English letters)
const isValidNameChar = (char) => {
    const arabicRegex = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/
    const englishRegex = /[a-zA-Z]/
    const spaceRegex = /\s/
    return arabicRegex.test(char) || englishRegex.test(char) || spaceRegex.test(char)
}

// Helper function to handle name input
const handleNameInput = (e, setFieldValue, fieldName) => {
    const value = e.target.value
    // Filter out invalid characters
    const filteredValue = value.split('').filter(char => isValidNameChar(char)).join('')
    setFieldValue(fieldName, filteredValue)
}

const generateDummyDate = type => {
    const currentDate = new Date()
    switch (type) {
        case 'SNR':
            return new Date(currentDate.setFullYear(currentDate.getFullYear() - 70)).toLocaleDateString('en-GB')
        case 'ADL':
            return new Date(currentDate.setFullYear(currentDate.getFullYear() - 30)).toLocaleDateString('en-GB')
        case 'CHD':
            return new Date(currentDate.setFullYear(currentDate.getFullYear() - 5)).toLocaleDateString('en-GB')
        case 'INF':
            return new Date(currentDate.setFullYear(currentDate.getFullYear() - 1)).toLocaleDateString('en-GB')
        default:
            return currentDate.toLocaleDateString('en-GB')
    }
}

export function BookingPassengersForm({ personInfo, initialPassengerDetails, onAddPassenger, routeType }) {
    const { t } = useTranslation()
    const DEFAULT_COUNTRY = 'bh'
    const DEFAULT_COUNTRY_CODE = '+973'
    const totalPassengers = Object.values(personInfo).reduce((acc, count) => acc + count, 0)
    const phoneUtil = PhoneNumberUtil.getInstance()
    
    // State for countries list
    const [countries, setCountries] = useState([])
    
    // State for file upload loading
    const [uploadingFiles, setUploadingFiles] = useState({})
    
    // Query to get countries list
    const [getCountries, { data, loading }] = useLazyQuery(GET_COUNTRY_LIST, {
        fetchPolicy: 'cache-first',
        onCompleted: (data) => {
            if (data?.country) {
                setCountries(data.country)
            }
        }
    })
    
    // Helper function to determine if trip is international
    const isInternationalTrip = () => {
        return routeType && routeType === 'INL' // INL is international, DMC is domestic
    }
    
    // Helper function to upload file to S3
    const uploadFileToS3 = async (file, passengerIndex, fieldName) => {
        const uploadKey = `${passengerIndex}-${fieldName}`
        setUploadingFiles(prev => ({ ...prev, [uploadKey]: true }))
        
        try {
            const fileName = file.name
            const fileSize = file.size
            const s3Key = generateUniqueS3Key('public/passenger/documents', fileName)
            
            const result = await uploadData({
                path: s3Key,
                data: file,
                options: {
                    accessLevel: 'public',
                },
            }).result
            
            const s3FileMetadata = {
                s3_key: s3Key,
                file_name: fileName,
                mime_type: file.type,
                size: fileSize,
            }
            
            setUploadingFiles(prev => ({ ...prev, [uploadKey]: false }))
            return s3FileMetadata
        } catch (error) {
            console.error('Error uploading file:', error)
            setUploadingFiles(prev => ({ ...prev, [uploadKey]: false }))
            return null
        }
    }

    // Load countries when component mounts for international trips
    useEffect(() => {
        if (isInternationalTrip()) {
            getCountries({ variables: { code: null } })
        }
    }, [routeType, getCountries])

    const initialValues = {
        email: initialPassengerDetails?.email || null,
        phoneNumber: initialPassengerDetails?.phoneNumber || '',
        passengers: [],
    }

    // Check if we have existing passenger data to preserve
    if (initialPassengerDetails?.passengers && initialPassengerDetails.passengers.length > 0) {
        // Use existing passenger data when navigating back from other tabs
        initialValues.passengers = initialPassengerDetails.passengers.map(passenger => ({
            ...passenger,
            // Ensure all required fields exist with proper defaults
            passenger: {
                ...passenger.passenger,
                special_assistance: passenger.passenger.special_assistance || (isInternationalTrip() ? {
                    wheelchair: false,
                    stroller: false,
                } : null),
                medical_condition: passenger.passenger.medical_condition || false,
                medical_remarks: passenger.passenger.medical_remarks || null,
            },
            seat_info: {
                ...passenger.seat_info,
            },
            // Preserve return seat info if it exists
            ...(passenger.return_seat_info && { return_seat_info: passenger.return_seat_info })
        }))
    } else {
        // Create fresh passenger objects only if no existing data
        const passengerTypes = ['ADL', 'CHD', 'INF', 'SNR']
        passengerTypes.forEach(type => {
            const count = personInfo[type] || 0
            for (let i = 0; i < count; i++) {
                const isFirstPassenger = i === 0 && initialValues.passengers.length === 0
                initialValues.passengers.push({
                    passenger: {
                        passenger_id: null,
                        given_name: isFirstPassenger ? '' : (isInternationalTrip() ? '' : 'No'),
                        family_name: isFirstPassenger ? '' : (isInternationalTrip() ? '' : 'Name'),
                        date_of_birth: isInternationalTrip() ? '' : generateDummyDate(type, true),                    nationality_code: isInternationalTrip() ? '' : null, // Set to null for domestic
                        passport_number: isInternationalTrip() ? '' : null, // Passport number for international trips
                        special_needs: isInternationalTrip() ? false : false, // Set to null for domestic
                        special_assistance: isInternationalTrip() ? {
                            wheelchair: false,
                            stroller: false,
                        } : null,
                        document_number: isInternationalTrip() ? '' : null, // National ID (conditional)
                        passport_attachment: null, // Passport attachment
                        visa_attachment: null, // Visa attachment (for non-GCC only)
                        document: null, // Legacy document attachment (optional)
                        medical_condition: false, // Medical condition checkbox
                        medical_remarks: null, // Medical condition details
                    },
                    seat_info: {
                        seat_no: null,
                        seat_type_code: null,
                        age_group_code: type,
                    },
                })
            }
        })
    }
    

    BookingPassengersForm.propTypes = {
        personInfo: PropTypes.object.isRequired,
        initialPassengerDetails: PropTypes.object.isRequired,
        onAddPassenger: PropTypes.func.isRequired,
        routeType: PropTypes.string,
    }

    const PassengersFormValidation = useFormik({
        enableReinitialize: true,
        initialValues,
        validationSchema: Yup.object().shape({
            email: Yup.string()
                .nullable()
                .transform(value => (!value ? null : value))
                .test('email', t('Invalid email format'), function(value) {
                    // Make email mandatory for first passenger in international trips
                    if (isInternationalTrip() && this.parent.passengers?.[0]) {
                        if (!value) return false // Required for first passenger
                        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
                    }
                    // Optional for others
                    if (!value) return true
                    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
                }),
            phoneNumber: Yup.string()
                .nullable()
                .test('isValidPhoneNumber', t('Invalid phone number'), function(value) {
                    // Phone number is always optional for domestic bookings
                    if (!isInternationalTrip()) {
                        // For domestic trips, phone is completely optional
                        if (!value || value.trim() === '' || value === DEFAULT_COUNTRY_CODE || value.trim() === '+973') {
                            return true // Optional for domestic - allow empty or just country code
                        }
                        // Only validate if user has entered a meaningful phone number
                        try {
                            const phoneNumber = parsePhoneNumberFromString(value)
                            return phoneNumber && phoneNumber.isValid()
                        } catch (error) {
                            return false
                        }
                    }
                    // Make phone number mandatory for first passenger in international trips
                    if (isInternationalTrip() && this.parent.passengers?.[0]) {
                        if (!value) return false // Required for first passenger
                        try {
                            const phoneNumber = parsePhoneNumberFromString(value)
                            return phoneNumber && phoneNumber.isValid()
                        } catch (error) {
                            return false
                        }
                    }
                    // Optional for others
                    if (!value) return true
                    try {
                        const phoneNumber = parsePhoneNumberFromString(value)
                        return phoneNumber && phoneNumber.isValid()
                    } catch (error) {
                        return false
                    }
                }),
            passengers: Yup.array().of(
                Yup.object().shape({
                    passenger: Yup.object().shape({
                        family_name: Yup.string()
                            .test('family_name_validation', t('Last Name is required'), function(value) {
                                const passengerIndex = this.path.match(/passengers\[(\d+)\]/)?.[1] || 0
                                // For international trips, all passengers need family name
                                if (isInternationalTrip()) {
                                    if (!value || value.trim() === '') return false
                                    return /^[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFFa-zA-Z\s]+$/.test(value.trim())
                                }
                                // For domestic trips, only passenger 1 (index 0) needs family name
                                if (parseInt(passengerIndex) === 0) {
                                    if (!value || value.trim() === '') return false
                                    return /^[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFFa-zA-Z\s]+$/.test(value.trim())
                                }
                                return true // Other passengers in domestic trips don't need validation
                            }),
                        given_name: Yup.string()
                            .test('given_name_validation', t('First Name is required'), function(value) {
                                const passengerIndex = this.path.match(/passengers\[(\d+)\]/)?.[1] || 0
                                // For international trips, all passengers need given name
                                if (isInternationalTrip()) {
                                    if (!value || value.trim() === '') return false
                                    return /^[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFFa-zA-Z\s]+$/.test(value.trim())
                                }
                                // For domestic trips, only passenger 1 (index 0) needs given name
                                if (parseInt(passengerIndex) === 0) {
                                    if (!value || value.trim() === '') return false
                                    return /^[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFFa-zA-Z\s]+$/.test(value.trim())
                                }
                                return true // Other passengers in domestic trips don't need validation
                            }),
                        // Date of birth and nationality required for international trips
                        ...(isInternationalTrip() && {
                            date_of_birth: Yup.string()
                                .required(t('Date of Birth is required'))
                                .test('date-format', t('Invalid date format. Please use DD/MM/YYYY'), value => {
                                    if (!value) return false
                                    // Check if the date matches DD/MM/YYYY format
                                    const dateRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/
                                    if (!dateRegex.test(value)) return false
                                    
                                    const [, day, month, year] = value.match(dateRegex)
                                    const date = new Date(year, month - 1, day)
                                    
                                    // Check if the date is valid
                                    return date.getDate() == day && 
                                           date.getMonth() == month - 1 && 
                                           date.getFullYear() == year &&
                                           date <= new Date() // Date should not be in the future
                                }),
                            nationality_code: Yup.string()
                                .required(t('Nationality is required')),
                            // Conditional validation based on nationality
                            passport_number: Yup.string()
                                .when('nationality_code', {
                                    is: (nationalityCode) => {
                                        // Passport required for non-GCC nationals
                                        return nationalityCode && !isGCCNationality(nationalityCode, countries)
                                    },
                                    then: (schema) => schema.required(t('Passport Number is required')).min(1, t('Passport Number cannot be empty')),
                                    otherwise: (schema) => schema.nullable().transform(value => (!value ? null : value))
                                }),
                                // National ID required for GCC nationals, optional for non-GCC
                                document_number: Yup.string()
                                .when('nationality_code', {
                                    is: (nationalityCode) => {
                                        // National ID required for GCC nationals
                                        return nationalityCode && isGCCNationality(nationalityCode, countries)
                                    },
                                    then: (schema) => schema.required(t('Passport / National ID is required')).min(1, t('Passport / National ID cannot be empty')),
                                    otherwise: (schema) => schema.nullable().transform(value => (!value ? null : value))
                                }),
                                // Attachment validations
                                passport_attachment: Yup.mixed()
                                .when('nationality_code', {
                                    is: (nationalityCode) => {
                                        return nationalityCode !== ''
                                    },
                                    then: (schema) => schema.required(t('Passport / National ID attachment is required')),
                                    otherwise: (schema) => schema.nullable()
                                }),
                                visa_attachment: Yup.mixed()
                                .when('nationality_code', {
                                    is: (nationalityCode) => {
                                        // Visa required for non-GCC nationals only
                                        return nationalityCode && !isGCCNationality(nationalityCode, countries)
                                    },
                                    then: (schema) => schema.required(t('Visa attachment is required')),
                                    otherwise: (schema) => schema.nullable()
                                }),
                                // Medical condition fields
                                medical_condition: Yup.boolean(),
                                medical_remarks: Yup.string()
                                    .nullable()
                                    .when('medical_condition', {
                                        is: true,
                                        then: (schema) => schema.required(t('Medical condition details are required')),
                                        otherwise: (schema) => schema.nullable()
                                    }),
                        }),
                    }),
                })
            ),
        }),
        onSubmit: (values, { setSubmitting, setFieldTouched }) => {
            // Mark all fields as touched to show validation errors
            setFieldTouched('email', true)
            setFieldTouched('phoneNumber', true)
            values.passengers.forEach((_, index) => {
                // For domestic trips, only validate passenger 1 (index 0)
                if (!isInternationalTrip() && index > 0) {
                    return // Skip validation for passengers beyond the first one in domestic trips
                }
                
                setFieldTouched(`passengers[${index}].passenger.given_name`, true)
                setFieldTouched(`passengers[${index}].passenger.family_name`, true)
                // Only mark international trip fields as touched for international trips
                if (isInternationalTrip()) {
                    setFieldTouched(`passengers[${index}].passenger.date_of_birth`, true)
                    setFieldTouched(`passengers[${index}].passenger.nationality_code`, true)
                    setFieldTouched(`passengers[${index}].passenger.passport_number`, true)
                    setFieldTouched(`passengers[${index}].passenger.document_number`, true)
                    setFieldTouched(`passengers[${index}].passenger.passport_attachment`, true)
                    setFieldTouched(`passengers[${index}].passenger.visa_attachment`, true)
                    setFieldTouched(`passengers[${index}].passenger.medical_condition`, true)
                    if (values.passengers[index].passenger.medical_condition) {
                        setFieldTouched(`passengers[${index}].passenger.medical_remarks`, true)
                    }
                }
            })
            
            // Only proceed if form is valid
            if (PassengersFormValidation.isValid) {
                onAddPassenger(values)
            }
            setSubmitting(false)
        },
        validateOnChange: true,
        validateOnBlur: true,
        validate: values => {
            const errors = {}
            // Add custom validation logic if needed
            return errors
        },
    })

    return (
        <FormikProvider value={PassengersFormValidation}>
            <form onSubmit={PassengersFormValidation.handleSubmit}>
                <div>
                    <p className="text-base font-semibold leading-7 text-gray-900 border-b-[0px] border-solid border-gray bg-[#f9f9f9] p-3 pb-2 mb-4">
                        {t('Contact Information')}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 mb-6 gap-4 sm:gap-6">
                        <div>
                            <label className="block text-sm font-medium leading-6 text-gray-900">
                                {t('Email')}
                                {isInternationalTrip() && <span className="text-red">*</span>}
                            </label>
                            <input
                                type="text"
                                name="email"
                                className="block w-full rounded-md border-0 py-2.5 sm:py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary text-sm sm:text-sm sm:leading-6"
                                placeholder="Email"
                                onChange={e => {
                                    const value = e.target.value.trim() || null
                                    PassengersFormValidation.setFieldValue('email', value)
                                }}
                                onBlur={() => {
                                    const value = PassengersFormValidation.values.email?.trim() || null
                                    PassengersFormValidation.setFieldValue('email', value)
                                    PassengersFormValidation.handleBlur('email')
                                }}
                                value={PassengersFormValidation.values.email || ''}
                            />
                            {PassengersFormValidation.touched.email && PassengersFormValidation.errors.email && (
                                <p className="text-error text-xs mt-1">{PassengersFormValidation.errors.email}</p>
                            )}
                        </div>
                        <div>
                            <label className="block text-sm font-medium leading-6 text-gray-900">
                                {t('Phone Number')}
                                {isInternationalTrip() ? (
                                    <span className="text-red">*</span>
                                ) : (
                                    <span className="text-gray-500 text-xs ml-1">({t('Optional')})</span>
                                )}
                            </label>
                            <PhoneInput
                                name="phoneNumber"
                                defaultCountry={DEFAULT_COUNTRY}
                                value={PassengersFormValidation.values.phoneNumber || ''}
                                onChange={phone => {
                                    // Set to null if phone is empty or just contains the default country code
                                    const phoneValue = !phone || phone === DEFAULT_COUNTRY_CODE ? null : phone
                                    PassengersFormValidation.setFieldValue('phoneNumber', phoneValue)
                                }}
                                onBlur={() => {
                                    // Clear the value if it's just the default country code
                                    if (PassengersFormValidation.values.phoneNumber === DEFAULT_COUNTRY_CODE) {
                                        PassengersFormValidation.setFieldValue('phoneNumber', null)
                                    }
                                    PassengersFormValidation.handleBlur('phoneNumber')
                                }}
                                placeholder=""
                                className="block w-full text-gray-900 shadow-sm placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                            />
                            {PassengersFormValidation.touched.phoneNumber &&
                                PassengersFormValidation.errors.phoneNumber && (
                                    <p className="text-error text-xs mt-1">
                                        {PassengersFormValidation.errors.phoneNumber}
                                    </p>
                                )}
                        </div>
                    </div>
                </div>

                {PassengersFormValidation.values.passengers.map((_, index) => {
                    const isFirstPassenger = index === 0
                    // For domestic bookings, only show passenger 1's name fields
                    const shouldShowPassenger = isInternationalTrip() || isFirstPassenger
                    
                    if (!shouldShowPassenger) {
                        return null
                    }
                    
                    return (
                        <div key={index} className="mb-4">
                            <p className="text-base font-semibold leading-7 text-gray-900 border-b-[0px] border-solid border-gray bg-[#f9f9f9] p-3 pb-2 mb-4">
                                {t('Passenger')} {index + 1}
                            </p>

                            <div className="grid grid-cols-1 sm:grid-cols-2 mb-6 gap-4 sm:gap-6">
                                <div>
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('First Name')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name={`passengers[${index}].passenger.given_name`}
                                        className="block w-full rounded-md border-0 py-2.5 sm:py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary text-sm sm:text-sm sm:leading-6"
                                        placeholder="First Name"
                                        onChange={(e) => handleNameInput(e, PassengersFormValidation.setFieldValue, `passengers[${index}].passenger.given_name`)}
                                        onBlur={PassengersFormValidation.handleBlur}
                                        value={
                                            PassengersFormValidation.values.passengers[index].passenger.given_name
                                        }
                                    />
                                    {PassengersFormValidation.touched.passengers?.[index]?.passenger?.given_name &&
                                        PassengersFormValidation.errors.passengers?.[index]?.passenger
                                            ?.given_name && (
                                        <p className="text-error text-xs mt-1">
                                            {
                                                PassengersFormValidation.errors.passengers[index].passenger
                                                    .given_name
                                            }
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Last Name')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name={`passengers[${index}].passenger.family_name`}
                                        className="block w-full rounded-md border-0 py-2.5 sm:py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary text-sm sm:text-sm sm:leading-6"
                                        placeholder="Last Name"
                                        onChange={(e) => handleNameInput(e, PassengersFormValidation.setFieldValue, `passengers[${index}].passenger.family_name`)}
                                        onBlur={PassengersFormValidation.handleBlur}
                                        value={
                                            PassengersFormValidation.values.passengers[index].passenger.family_name
                                        }
                                    />
                                    {PassengersFormValidation.touched.passengers?.[index]?.passenger?.family_name &&
                                        PassengersFormValidation.errors.passengers?.[index]?.passenger
                                            ?.family_name && (
                                        <p className="text-error text-xs mt-1">
                                            {
                                                PassengersFormValidation.errors.passengers[index].passenger
                                                    .family_name
                                            }
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* International trip fields - Date of Birth, Nationality, Passport Number */}
                            {isInternationalTrip() && (
                                <>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 mb-6 gap-4 sm:gap-6">
                                        <div>
                                            <label className="block text-sm font-medium leading-6 text-gray-900">
                                                {t('Date of Birth')}
                                                <span className="text-red">*</span>
                                            </label>
                                            <div className="relative">
                                                <Flatpickr
                                                    name={`passengers[${index}].passenger.date_of_birth`}
                                                    value={PassengersFormValidation.values.passengers[index].passenger.date_of_birth}
                                                    onChange={([date]) => {
                                                        const formattedDate = date ? date.toLocaleDateString('en-GB') : ''
                                                        PassengersFormValidation.setFieldValue(`passengers[${index}].passenger.date_of_birth`, formattedDate)
                                                    }}
                                                    options={{
                                                        dateFormat: 'd/m/Y',
                                                        maxDate: new Date(),
                                                        allowInput: true,
                                                    }}
                                                    className="block w-full rounded-md border-0 py-2.5 sm:py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary text-sm sm:text-sm sm:leading-6 pr-10"
                                                    placeholder="DD/MM/YYYY"
                                                />
                                                <CalendarIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                            </div>
                                            {PassengersFormValidation.touched.passengers?.[index]?.passenger?.date_of_birth &&
                                                PassengersFormValidation.errors.passengers?.[index]?.passenger?.date_of_birth && (
                                                <p className="text-error text-xs mt-1">
                                                    {PassengersFormValidation.errors.passengers[index].passenger.date_of_birth}
                                                </p>
                                            )}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium leading-6 text-gray-900">
                                                {t('Nationality')}
                                                <span className="text-red">*</span>
                                            </label>
                                            <select
                                                name={`passengers[${index}].passenger.nationality_code`}
                                                value={PassengersFormValidation.values.passengers[index].passenger.nationality_code}
                                                onChange={PassengersFormValidation.handleChange}
                                                onBlur={PassengersFormValidation.handleBlur}
                                                className="block w-full rounded-md border-0 py-2.5 sm:py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                            >
                                                <option value="">{t('Select Nationality')}</option>
                                                {countries.map((country) => (
                                                    <option key={country.alpha_iso2} value={country.alpha_iso2}>
                                                        {country.country}
                                                    </option>
                                                ))}
                                            </select>
                                            {PassengersFormValidation.touched.passengers?.[index]?.passenger?.nationality_code &&
                                                PassengersFormValidation.errors.passengers?.[index]?.passenger?.nationality_code && (
                                                <p className="text-error text-xs mt-1">
                                                    {PassengersFormValidation.errors.passengers[index].passenger.nationality_code}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    
                                    <div className="grid grid-cols-1 sm:grid-cols-2 mb-6 gap-4 sm:gap-6">
                                        {/* Passport Number - Required for non-GCC nationals only */}
                                        {PassengersFormValidation.values.passengers[index].passenger.nationality_code && 
                                         !isGCCNationality(PassengersFormValidation.values.passengers[index].passenger.nationality_code, countries) && (
                                            <div>
                                                <label className="block text-sm font-medium leading-6 text-gray-900">
                                                    {t('Passport Number')}
                                                    <span className="text-red">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    name={`passengers[${index}].passenger.passport_number`}
                                                    className="block w-full rounded-md border-0 py-2.5 sm:py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary text-sm sm:text-sm sm:leading-6"
                                                    placeholder="Passport Number"
                                                    onChange={PassengersFormValidation.handleChange}
                                                    onBlur={PassengersFormValidation.handleBlur}
                                                    value={
                                                        PassengersFormValidation.values.passengers[index].passenger.passport_number || ''
                                                    }
                                                />
                                                {PassengersFormValidation.touched.passengers?.[index]?.passenger?.passport_number &&
                                                    PassengersFormValidation.errors.passengers?.[index]?.passenger?.passport_number && (
                                                    <p className="text-error text-xs mt-1">
                                                        {PassengersFormValidation.errors.passengers[index].passenger.passport_number}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                        
                                        {/* Passport/National ID field for GCC nationals */}
                                        {PassengersFormValidation.values.passengers[index].passenger.nationality_code && 
                                         isGCCNationality(PassengersFormValidation.values.passengers[index].passenger.nationality_code, countries) && (
                                            <div>
                                                <label className="block text-sm font-medium leading-6 text-gray-900">
                                                    {t('Passport / National ID')}
                                                    <span className="text-red">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    name={`passengers[${index}].passenger.document_number`}
                                                    className="block w-full rounded-md border-0 py-2.5 sm:py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary text-sm sm:text-sm sm:leading-6"
                                                    placeholder="Passport / National ID"
                                                    onChange={PassengersFormValidation.handleChange}
                                                    onBlur={PassengersFormValidation.handleBlur}
                                                    value={
                                                        PassengersFormValidation.values.passengers[index].passenger.document_number || ''
                                                    }
                                                />
                                                {PassengersFormValidation.touched.passengers?.[index]?.passenger?.document_number &&
                                                    PassengersFormValidation.errors.passengers?.[index]?.passenger?.document_number && (
                                                    <p className="text-error text-xs mt-1">
                                                        {PassengersFormValidation.errors.passengers[index].passenger.document_number}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Attachment Section */}
                                    {PassengersFormValidation.values.passengers[index].passenger.nationality_code && (
                                        <div className="mb-6">
                                            <p className="text-base font-semibold leading-7 text-gray-900 border-b-[0px] border-solid border-gray bg-[#f9f9f9] p-3 pb-2 mb-4">
                                                {t('Attachment')}
                                            </p>
                                            
                                            {/* Passport/National ID Attachment - Required for all */}
                                            <div className="mb-4">
                                                <label className="block text-sm font-medium leading-6 text-gray-900 mb-2">
                                                    {isGCCNationality(PassengersFormValidation.values.passengers[index].passenger.nationality_code, countries) 
                                                        ? t('Passport / National ID') 
                                                        : t('Passport')
                                                    }
                                                    <span className="text-red">*</span>
                                                </label>
                                                
                                                {/* Show file preview if file is selected */}
                                                {PassengersFormValidation.values.passengers[index].passenger.passport_attachment ? (
                                                    <div className="mt-2">
                                                        <div className="rounded-lg border border-gray-300 p-4">
                                                            <div className="flex items-start space-x-4">
                                                                {/* Image preview or file icon */}
                                                                <div className="flex-shrink-0">
                                                                    {PassengersFormValidation.values.passengers[index].passenger.passport_attachment.mime_type?.startsWith('image/') ? (
                                                                        <div className="h-20 w-20 rounded-lg bg-gray-100 flex items-center justify-center">
                                                                            <DocumentArrowUpIcon className="h-8 w-8 text-gray-400" />
                                                                        </div>
                                                                    ) : (
                                                                        <div className="h-20 w-20 rounded-lg bg-gray-100 flex items-center justify-center">
                                                                            <DocumentArrowUpIcon className="h-8 w-8 text-gray-400" />
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                
                                                                {/* File information */}
                                                                <div className="flex-1 min-w-0">
                                                                    <p className="text-sm font-medium text-gray-900 truncate">
                                                                        {PassengersFormValidation.values.passengers[index].passenger.passport_attachment.file_name}
                                                                    </p>
                                                                    <p className="text-sm text-gray-500">
                                                                        {(PassengersFormValidation.values.passengers[index].passenger.passport_attachment.size / 1024 / 1024).toFixed(2)} MB
                                                                    </p>
                                                                    <p className="text-xs text-gray-400 mt-1">
                                                                        {PassengersFormValidation.values.passengers[index].passenger.passport_attachment.mime_type}
                                                                    </p>
                                                                </div>
                                                                
                                                                {/* Remove button with cross icon */}
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        PassengersFormValidation.setFieldValue(`passengers[${index}].passenger.passport_attachment`, null)
                                                                    }}
                                                                    className="flex-shrink-0 p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-full transition-colors"
                                                                    title={t('Remove file')}
                                                                >
                                                                    <XMarkIcon className="h-5 w-5" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    /* Upload area when no file is selected */
                                                    <div className="mt-2 flex justify-center rounded-lg border border-dashed border-gray-900/25 px-6 py-10">
                                                        <div className="text-center">
                                                            <DocumentArrowUpIcon className="mx-auto h-12 w-12 text-gray-300" aria-hidden="true" />
                                                            <div className="mt-4 flex text-sm leading-6 text-gray-600">
                                                                <label
                                                                    htmlFor={`passport-upload-${index}`}
                                                                    className="relative cursor-pointer rounded-md bg-white font-semibold text-primary focus-within:outline-none focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2 hover:text-primary/80"
                                                                >
                                                                    <span>{uploadingFiles[`${index}-passport_attachment`] ? t('Uploading...') : t('Upload a file')}</span>
                                                                    <input
                                                                        id={`passport-upload-${index}`}
                                                                        name={`passengers[${index}].passenger.passport_attachment`}
                                                                        type="file"
                                                                        className="sr-only"
                                                                        accept=".pdf,.jpg,.jpeg,.png"
                                                                        disabled={uploadingFiles[`${index}-passport_attachment`]}
                                                                        onChange={async (e) => {
                                                                            const file = e.target.files?.[0] || null
                                                                            if (file) {
                                                                                const s3FileMetadata = await uploadFileToS3(file, index, 'passport_attachment')
                                                                                if (s3FileMetadata) {
                                                                                    PassengersFormValidation.setFieldValue(`passengers[${index}].passenger.passport_attachment`, s3FileMetadata)
                                                                                }
                                                                            } else {
                                                                                PassengersFormValidation.setFieldValue(`passengers[${index}].passenger.passport_attachment`, null)
                                                                            }
                                                                        }}
                                                                    />
                                                                </label>
                                                                <p className="pl-1">{t('or drag and drop')}</p>
                                                            </div>
                                                            <p className="text-xs leading-5 text-gray-600">{t('PDF, PNG, JPG up to 10MB')}</p>
                                                        </div>
                                                    </div>
                                                )}
                                                {PassengersFormValidation.touched.passengers?.[index]?.passenger?.passport_attachment &&
                                                    PassengersFormValidation.errors.passengers?.[index]?.passenger?.passport_attachment && (
                                                    <p className="text-error text-xs mt-1">
                                                        {PassengersFormValidation.errors.passengers[index].passenger.passport_attachment}
                                                    </p>
                                                )}
                                            </div>
                                            
                                            {/* Visa Attachment - Required for Non-GCC nationals only */}
                                            {!isGCCNationality(PassengersFormValidation.values.passengers[index].passenger.nationality_code, countries) && (
                                                <div className="mb-4">
                                                    <label className="block text-sm font-medium leading-6 text-gray-900 mb-2">
                                                        {t('Visa')}
                                                        <span className="text-red">*</span>
                                                    </label>
                                                    
                                                    {/* Show file preview if file is selected */}
                                                    {PassengersFormValidation.values.passengers[index].passenger.visa_attachment ? (
                                                        <div className="mt-2">
                                                            <div className="rounded-lg border border-gray-300 p-4">
                                                                <div className="flex items-start space-x-4">
                                                                    {/* Image preview or file icon */}
                                                                    <div className="flex-shrink-0">
                                                                        {PassengersFormValidation.values.passengers[index].passenger.visa_attachment.mime_type?.startsWith('image/') ? (
                                                                            <div className="h-20 w-20 rounded-lg bg-gray-100 flex items-center justify-center">
                                                                                <DocumentArrowUpIcon className="h-8 w-8 text-gray-400" />
                                                                            </div>
                                                                        ) : (
                                                                            <div className="h-20 w-20 rounded-lg bg-gray-100 flex items-center justify-center">
                                                                                <DocumentArrowUpIcon className="h-8 w-8 text-gray-400" />
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    
                                                                    {/* File information */}
                                                                    <div className="flex-1 min-w-0">
                                                                        <p className="text-sm font-medium text-gray-900 truncate">
                                                                            {PassengersFormValidation.values.passengers[index].passenger.visa_attachment.file_name}
                                                                        </p>
                                                                        <p className="text-sm text-gray-500">
                                                                            {(PassengersFormValidation.values.passengers[index].passenger.visa_attachment.size / 1024 / 1024).toFixed(2)} MB
                                                                        </p>
                                                                        <p className="text-xs text-gray-400 mt-1">
                                                                            {PassengersFormValidation.values.passengers[index].passenger.visa_attachment.mime_type}
                                                                        </p>
                                                                    </div>
                                                                    
                                                                    {/* Remove button with cross icon */}
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            PassengersFormValidation.setFieldValue(`passengers[${index}].passenger.visa_attachment`, null)
                                                                        }}
                                                                        className="flex-shrink-0 p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-full transition-colors"
                                                                        title={t('Remove file')}
                                                                    >
                                                                        <XMarkIcon className="h-5 w-5" />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        /* Upload area when no file is selected */
                                                        <div className="mt-2 flex justify-center rounded-lg border border-dashed border-gray-900/25 px-6 py-10">
                                                            <div className="text-center">
                                                                <DocumentArrowUpIcon className="mx-auto h-12 w-12 text-gray-300" aria-hidden="true" />
                                                                <div className="mt-4 flex text-sm leading-6 text-gray-600">
                                                                    <label
                                                                        htmlFor={`visa-upload-${index}`}
                                                                        className="relative cursor-pointer rounded-md bg-white font-semibold text-primary focus-within:outline-none focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2 hover:text-primary/80"
                                                                    >
                                                                        <span>{uploadingFiles[`${index}-visa_attachment`] ? t('Uploading...') : t('Upload a file')}</span>
                                                                        <input
                                                                            id={`visa-upload-${index}`}
                                                                            name={`passengers[${index}].passenger.visa_attachment`}
                                                                            type="file"
                                                                            className="sr-only"
                                                                            accept=".pdf,.jpg,.jpeg,.png"
                                                                            disabled={uploadingFiles[`${index}-visa_attachment`]}
                                                                            onChange={async (e) => {
                                                                                const file = e.target.files?.[0] || null
                                                                                if (file) {
                                                                                    const s3FileMetadata = await uploadFileToS3(file, index, 'visa_attachment')
                                                                                    if (s3FileMetadata) {
                                                                                        PassengersFormValidation.setFieldValue(`passengers[${index}].passenger.visa_attachment`, s3FileMetadata)
                                                                                    }
                                                                                } else {
                                                                                    PassengersFormValidation.setFieldValue(`passengers[${index}].passenger.visa_attachment`, null)
                                                                                }
                                                                            }}
                                                                        />
                                                                    </label>
                                                                    <p className="pl-1">{t('or drag and drop')}</p>
                                                                </div>
                                                                <p className="text-xs leading-5 text-gray-600">{t('PDF, PNG, JPG up to 10MB')}</p>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {PassengersFormValidation.touched.passengers?.[index]?.passenger?.visa_attachment &&
                                                        PassengersFormValidation.errors.passengers?.[index]?.passenger?.visa_attachment && (
                                                        <p className="text-error text-xs mt-1">
                                                            {PassengersFormValidation.errors.passengers[index].passenger.visa_attachment}
                                                        </p>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Medical Condition Section */}
                                    <div className="mb-6">
                                        <div className={`  ${PassengersFormValidation.values.passengers[index].passenger.medical_condition ? '' : ''}`}>
                                            <div className="flex items-center mb-3">
                                                <input
                                                    type="checkbox"
                                                    name={`passengers[${index}].passenger.medical_condition`}
                                                    checked={PassengersFormValidation.values.passengers[index].passenger.medical_condition}
                                                    onChange={(e) => {
                                                        PassengersFormValidation.setFieldValue(`passengers[${index}].passenger.medical_condition`, e.target.checked)
                                                        if (!e.target.checked) {
                                                            PassengersFormValidation.setFieldValue(`passengers[${index}].passenger.medical_remarks`, null)
                                                        }
                                                    }}
                                                    className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                                                />
                                                <label className="ml-2 flex items-center text-sm text-gray-900">
                                                    {t('Medical Condition')}
                                                    {PassengersFormValidation.values.passengers[index].passenger.medical_condition && (
                                                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                                            {t('Medical Attention Required')}
                                                        </span>
                                                    )}
                                                </label>
                                            </div>

                                            {/* Medical Remarks - only show if medical condition is checked */}
                                            {PassengersFormValidation.values.passengers[index].passenger.medical_condition && (
                                                <div className="mt-3">
                                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                                        {t('Medical Condition Details')}
                                                        <span className="text-red">*</span>
                                                    </label>
                                                    <textarea
                                                        name={`passengers[${index}].passenger.medical_remarks`}
                                                        rows={3}
                                                        className="block w-full rounded-md border-0 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary text-sm sm:text-sm sm:leading-6"
                                                        placeholder={t('Please provide details about the medical condition')}
                                                        value={PassengersFormValidation.values.passengers[index].passenger.medical_remarks || ''}
                                                        onChange={PassengersFormValidation.handleChange}
                                                        onBlur={PassengersFormValidation.handleBlur}
                                                    />
                                                    {PassengersFormValidation.touched.passengers?.[index]?.passenger?.medical_remarks &&
                                                        PassengersFormValidation.errors.passengers?.[index]?.passenger?.medical_remarks && (
                                                        <p className="text-error text-xs mt-1">
                                                            {PassengersFormValidation.errors.passengers[index].passenger.medical_remarks}
                                                        </p>
                                                    )}
                                                    <p className="mt-2 text-xs text-gray-500">
                                                        {t('This information will be shared with the relevant staff to ensure appropriate medical attention if needed.')}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* Special Assistance - only show for international trips */}
                            {isInternationalTrip() && (
                                <div className="mb-6">
                                    <div className="flex items-center mb-3">
                                        <input
                                            type="checkbox"
                                            name={`passengers[${index}].passenger.special_needs`}
                                            checked={PassengersFormValidation.values.passengers[index].passenger.special_needs}
                                            onChange={(e) => {
                                                PassengersFormValidation.setFieldValue(`passengers[${index}].passenger.special_needs`, e.target.checked)
                                                // Reset special assistance options when unchecked
                                                if (!e.target.checked) {
                                                    PassengersFormValidation.setFieldValue(`passengers[${index}].passenger.special_assistance.wheelchair`, false)
                                                    PassengersFormValidation.setFieldValue(`passengers[${index}].passenger.special_assistance.stroller`, false)
                                                }
                                            }}
                                            className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                                        />
                                        <label className="ml-2 block text-sm text-gray-900">
                                            {t('Special Assistance')}
                                        </label>
                                    </div>

                                    {/* Special Assistance Options - only show if special assistance is selected */}
                                    {PassengersFormValidation.values.passengers[index].passenger.special_needs && (
                                        <div className="ml-6 space-y-3">
                                            <div className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    name={`passengers[${index}].passenger.special_assistance.wheelchair`}
                                                    checked={PassengersFormValidation.values.passengers[index].passenger.special_assistance.wheelchair}
                                                    onChange={PassengersFormValidation.handleChange}
                                                    className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                                                />
                                                <label className="ml-2 block text-sm text-gray-900">
                                                    {t('Wheelchair')}
                                                </label>
                                            </div>
                                            <div className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    name={`passengers[${index}].passenger.special_assistance.stroller`}
                                                    checked={PassengersFormValidation.values.passengers[index].passenger.special_assistance.stroller}
                                                    onChange={PassengersFormValidation.handleChange}
                                                    className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                                                />
                                                <label className="ml-2 block text-sm text-gray-900">
                                                    {t('Stroller')}
                                                </label>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )
                })}
                <div className="flex justify-end bg-primary/[.06] p-4 rounded-md">
                    <button type="submit" className="px-6 py-3 sm:py-2 bg-teal-600 text-white rounded-md text-sm sm:text-base font-medium min-h-[44px] sm:min-h-auto">
                        {t('Continue')}
                    </button>
                </div>
            </form>
        </FormikProvider>
    )
}
